'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  Heading,
  InlineMessage,
  Link,
  TextField,
} from '@invisionag/components';
import { useTranslation } from '@invisionag/next/i18n';
import { Result } from '@invisionag/next/result';
import { CenteredContainer } from '../../../_components/centered-container';
import { isHostValid } from '@/lib/utils';
import { Form, FormMessage, FormResult, SubmitButton } from '@/lib/form';
import * as Cortex from '@/repositories/cortex';
import { Divider } from '@/app/(public)/_components/divider';

type AuthenticationError = 'auth-failed' | 'request-expired';

export function LoginSSOForm() {
  const { t } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('return_url');
  const [authError, setAuthError] = useState<AuthenticationError | undefined>(
    () => {
      const error = searchParams.get('error');

      if (error === 'auth-failed' || error === 'request-expired') {
        return error;
      }
      return undefined;
    },
  );

  useEffect(() => {
    const filteredParams = new URLSearchParams(searchParams);
    if (filteredParams.has('error')) {
      filteredParams.delete('error');
      router.replace(`/login/sso?${filteredParams.toString()}`);
    }
  }, [router, searchParams]);

  async function login(formData: FormData): Promise<FormResult> {
    setAuthError(undefined);
    const email = assertString(formData.get('email'));

    if (!email) {
      return Result.Err({
        validationErrors: {
          email: t('login-sso.form.field.email.validation.valueMissing'),
        },
      });
    }

    const result = await Cortex.loginSSO(
      email,
      returnUrl && isHostValid(returnUrl) ? returnUrl : undefined,
    );

    if (!result.ok) {
      if (result.err === 'invalid') {
        router.push('/login');
        return Result.Ok();
      }

      return Result.Err({
        message: t(`login-sso.form.error.${result.err}`),
      });
    }

    router.push(result.val.redirectUrl);
    return Result.Ok();
  }

  return (
    <CenteredContainer>
      <Heading level={1} className="text-center">
        {t('login-sso.form.heading')}
      </Heading>
      <Form className="grid gap-4" action={login}>
        <div className="grid gap-4">
          <TextField
            isRequired
            autoFocus={true}
            label={t('login-sso.form.field.email.label')}
            name="email"
            type="email"
          />
        </div>
        <FormMessage />
        {authError && (
          <InlineMessage variant="error">
            {t(`login-sso.form.error.${authError}`)}
          </InlineMessage>
        )}
        <SubmitButton className="w-full">
          {t('login-sso.form.submit')}
        </SubmitButton>
      </Form>
      <Divider label={t('login.divider.label')} />
      <Link
        className="justify-self-center"
        href={`/login?${searchParams.toString()}`}
      >
        {t('login-sso.form.login-link')}
      </Link>
    </CenteredContainer>
  );
}

function assertString(value: unknown): string {
  if (typeof value === 'string') return value;
  throw new Error(`Expected string, got '${typeof value}'`);
}
